<template>
  <!-- User Permissions Modal -->
  <div class="modal fade" id="userPermissionsModal" tabindex="-1" aria-labelledby="userPermissionsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="userPermissionsModalLabel">
            {{ $t('users.editPermissions') }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        
        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ $t('common.loading') }}</span>
            </div>
          </div>
          
          <!-- Error State -->
          <div v-else-if="error" class="alert alert-danger" role="alert">
            <i class="ti ti-alert-circle me-2"></i>
            {{ error }}
          </div>
          
          <!-- Content when loaded successfully -->
          <div v-else>
            <!-- Search and Controls -->
            <div class="permission-controls mb-3">
              <div class="row align-items-center">
                <div class="col-md-6">
                  <div class="input-group input-group-sm">
                    <span class="input-group-text">
                      <i class="ti ti-search"></i>
                    </span>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      placeholder="Search"
                      v-model="searchQuery"
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="d-flex gap-1 justify-content-end">
                    <button 
                      type="button" 
                      class="btn btn-outline-primary btn-sm px-2 py-1"
                      @click="expandAllGroups"
                      style="font-size: 0.75rem; white-space: nowrap;"
                    >
                      <i class="ti ti-chevrons-down me-1"></i>
                      Expand All
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-outline-secondary btn-sm px-2 py-1"
                      @click="collapseAllGroups"
                      style="font-size: 0.75rem; white-space: nowrap;"
                    >
                      <i class="ti ti-chevrons-up me-1"></i>
                      Collapse All
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Permissions List -->
            <div v-if="Object.keys(filteredGroupedPermissions).length > 0">
              <!-- Permission Groups Tree View -->
              <div class="permission-tree">
                <div v-for="(permissions, groupName, index) in filteredGroupedPermissions" :key="groupName" 
                     class="permission-group mb-3"
                     draggable="true"
                     @dragstart="onDragStart($event, groupName)"
                     @dragend="onDragEnd($event)"
                     @dragover="onDragOver($event)"
                     @dragenter="onDragEnter($event, groupName)"
                     @dragleave="onDragLeave($event)"
                     @drop="onDrop($event, groupName)"
                >
                  <div 
                    class="group-header" 
                    :class="{ expanded: expandedGroups[index] }"
                  >
                    <div class="d-flex align-items-center">
                      <div class="group-toggle" @click="toggleGroup(index)">
                        <i 
                          class="toggle-icon me-2" 
                          :class="expandedGroups[index] ? 'ti ti-chevron-down' : 'ti ti-chevron-right'"
                        ></i>
                        <span class="group-title fw-semibold">
                          {{ getGroupDisplayName(groupName, permissions) }}
                        </span>
                      </div>
                      
                      <div class="ms-auto d-flex align-items-center gap-2">
                        <!-- Show parent permission toggle if this is a parent group -->
                        <div v-if="isParentGroup(groupName)" class="form-check form-switch me-3">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`parent-${groupName}`"
                            v-model="getParentPermission(groupName).isGranted"
                            @change="togglePermission(getParentPermission(groupName))"
                            :disabled="updating"
                          />
                          <label class="form-check-label small" :for="`parent-${groupName}`">
                            Enable
                          </label>
                        </div>
                        
                        <!-- Show Select All for all groups -->
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`group-select-${index}`"
                            :checked="isGroupFullySelected(permissions)"
                            :indeterminate="isGroupPartiallySelected(permissions)"
                            @change="toggleGroupSelection(permissions, $event)"
                            :disabled="updating || (isParentGroup(groupName) && !getParentPermission(groupName).isGranted)"
                          />
                          <label class="form-check-label small" :for="`group-select-${index}`">
                            {{ $t('users.permissions.selectAll') }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="group-content" v-show="expandedGroups[index]">
                    <div class="permission-items">
                      <div v-for="permission in getChildPermissions(permissions)" :key="permission.name" 
                           class="permission-item"
                           :class="{ disabled: !canGrantPermission(permission) && !permission.isGranted }">
                        <div class="d-flex align-items-center">
                          <div class="flex-grow-1">
                            <div class="permission-name">{{ permission.displayName || permission.name }}</div>
                            <div v-if="!canGrantPermission(permission) && !permission.isGranted" class="small text-warning mt-1">
                              <i class="ti ti-lock me-1"></i>
                              Parent permission required
                            </div>
                          </div>
                          <div class="form-check form-switch">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :id="`permission-${permission.name}`"
                              v-model="permission.isGranted"
                              @change="togglePermission(permission)"
                              :disabled="updating || !canGrantPermission(permission)"
                            />
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Empty State -->
            <div v-else class="text-center py-4">
              <div class="mb-3">
                <i class="ti ti-shield-off text-muted" style="font-size: 3rem;"></i>
              </div>
              <h6 class="text-muted">{{ $t('users.permissions.noPermissions') }}</h6>
              <p class="text-muted">{{ $t('users.permissions.noPermissionsDescription') }}</p>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            {{ $t('common.actions.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import userPermissionService from '@/services/userPermissionService';
import userService from '@/services/userService';
import superAdminService from '@/services/superAdminService';
import Swal from 'sweetalert2';

const { t } = useI18n();

const props = defineProps({
  userId: {
    type: String,
    default: null
  },
  show: {
    type: Boolean,
    default: false
  },
  isSuperAdmin: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'error']);

const loading = ref(false);
const updating = ref(false);
const error = ref('');
const permissions = ref([]);
const user = ref(null);
const expandedGroups = ref({});
const searchQuery = ref('');
const groupOrder = ref([]);

// Load group order from localStorage
const loadGroupOrder = () => {
  const saved = localStorage.getItem('permissionGroupOrder');
  if (saved) {
    groupOrder.value = JSON.parse(saved);
  }
};

// Save group order to localStorage
const saveGroupOrder = () => {
  localStorage.setItem('permissionGroupOrder', JSON.stringify(groupOrder.value));
};

// Helper: Create groups for parent permissions
const createParentGroups = (groups) => {
  const parentPermissions = permissions.value.filter(p => !p.parentName);
  parentPermissions.forEach(parent => {
    groups[parent.name] = [parent];
  });
  return groups;
};

// Helper: Add child permissions to their parent groups
const addChildPermissionsToGroups = (groups) => {
  permissions.value.forEach(permission => {
    if (!permission.parentName) return;
    
    const parentPermission = permissions.value.find(p => p.name === permission.parentName);
    if (!parentPermission) return;
    
    if (!groups[permission.parentName]) {
      groups[permission.parentName] = [];
    }
    groups[permission.parentName].push(permission);
  });
  return groups;
};

// Helper: Handle orphaned permissions
const handleOrphanedPermissions = (groups) => {
  permissions.value.forEach(permission => {
    if (!permission.parentName) return;
    
    const parentExists = permissions.value.some(p => p.name === permission.parentName);
    if (parentExists) return;
    
    if (!groups['System']) {
      groups['System'] = [];
    }
    groups['System'].push(permission);
  });
  return groups;
};

// Helper: Apply custom order to groups
const applyCustomOrder = (groups) => {
  if (groupOrder.value.length === 0) {
    return groups;
  }
  
  const orderedGroups = {};
  
  // Add groups in saved order
  groupOrder.value.forEach(groupName => {
    if (groups[groupName]) {
      orderedGroups[groupName] = groups[groupName];
    }
  });
  
  // Add any new groups that weren't in saved order
  Object.keys(groups).forEach(groupName => {
    if (!orderedGroups[groupName]) {
      orderedGroups[groupName] = groups[groupName];
    }
  });
  
  return orderedGroups;
};

// Group permissions by parentName with custom order
const groupedPermissions = computed(() => {
  const groups = {};
  
  createParentGroups(groups);
  addChildPermissionsToGroups(groups);
  handleOrphanedPermissions(groups);
  
  return applyCustomOrder(groups);
});

// Check if a permission is an actual permission (not a group header)
const isActualPermission = (permission) => {
  // All permissions are actual permissions - we'll treat parent items as permissions
  // that control access to their children
  return true;
};

// Get parent permissions (those with no parentName)
const parentPermissions = computed(() => {
  return permissions.value.filter(p => !p.parentName);
});

// Check if a permission's parent is granted
const isParentGranted = (permissionName) => {
  const permission = permissions.value.find(p => p.name === permissionName);
  if (!permission?.parentName) return true; // If no parent, it's allowed
  
  const parent = permissions.value.find(p => p.name === permission.parentName);
  return parent ? parent.isGranted : false;
};

// Check if a permission can be granted (parent dependency check)
const canGrantPermission = (permission) => {
  // If no parentName, it's a top-level permission that can be toggled
  if (!permission.parentName) return true;
  
  // Check if parent is granted
  return isParentGranted(permission.name);
};

const filteredGroupedPermissions = computed(() => {
  const filteredGroups = {};
  for (const groupName in groupedPermissions.value) {
    const filteredPermissions = groupedPermissions.value[groupName].filter(permission => {
      const nameMatch = permission.name.toLowerCase().includes(searchQuery.value.toLowerCase());
      const displayNameMatch = permission.displayName?.toLowerCase().includes(searchQuery.value.toLowerCase());
      return nameMatch || displayNameMatch;
    });
    
    // Only include groups that have matching permissions
    if (filteredPermissions.length > 0) {
      filteredGroups[groupName] = filteredPermissions;
    }
  }
  return filteredGroups;
});

const getActiveCount = (permissions) => {
  return permissions.filter(p => p.isGranted).length;
};

const loadUserPermissions = async () => {
  if (!props.userId) {
    error.value = t('users.permissions.invalidUser');
    return;
  }
  
  loading.value = true;
  error.value = '';
  
  try {
    // Use super admin services when in super admin context
    if (props.isSuperAdmin) {
      const permissionsResponse = await superAdminService.getUserPermissions(props.userId);
      user.value = { id: props.userId }; // Minimal user object for super admin context
      permissions.value = permissionsResponse || [];
    } else {
      // Load user info and permissions in parallel for regular context
      const [userResponse, permissionsResponse] = await Promise.all([
        userService.getTenantUserById(props.userId),
        userPermissionService.getUserPermissions(props.userId)
      ]);
      
      user.value = userResponse;
      permissions.value = permissionsResponse || [];
    }
    
    // Load saved group order
    loadGroupOrder();
    
    // If no saved order exists, create initial order from current groups
    if (groupOrder.value.length === 0) {
      const groups = {};
      permissions.value.forEach(permission => {
        const groupName = permission.parentName || 'System';
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
      });
      groupOrder.value = Object.keys(groups);
      saveGroupOrder();
    }
  } catch (err) {
    console.error('Error loading user permissions:', err);
    error.value = t('users.permissions.loadFailed');
  } finally {
    loading.value = false;
  }
};

// Helper: Get the appropriate permission service
const getPermissionService = () => {
  return props.isSuperAdmin ? superAdminService : userPermissionService;
};

// Helper: Check if permission grant is valid
const validatePermissionGrant = (permission) => {
  if (permission.isGranted && !canGrantPermission(permission)) {
    Swal.fire({
      title: t('common.actions.warning'),
      text: t('users.permissions.parentRequired'),
      icon: 'warning'
    });
    permission.isGranted = false;
    return false;
  }
  return true;
};

// Helper: Grant a permission
const grantPermission = async (permission) => {
  const service = getPermissionService();
  await service.grantPermission(props.userId, permission.name);
  
  Swal.fire({
    title: t('common.actions.success'),
    text: t('users.permissions.grantSuccess', { permission: permission.displayName || permission.name }),
    icon: 'success',
    timer: 2000,
    showConfirmButton: false
  });
};

// Helper: Get child permissions that are granted
const getGrantedChildPermissions = (permission) => {
  return permissions.value.filter(p => 
    p.parentName === permission.name && p.isGranted
  );
};

// Helper: Revoke child permissions
const revokeChildPermissions = async (childPermissions) => {
  const service = getPermissionService();
  
  for (const childPermission of childPermissions) {
    try {
      await service.revokePermission(props.userId, childPermission.name);
      childPermission.isGranted = false;
    } catch (err) {
      console.error('Error revoking child permission:', err);
    }
  }
};

// Helper: Confirm and revoke parent permission with children
const confirmAndRevokeParent = async (permission) => {
  const childPermissions = getGrantedChildPermissions(permission);
  
  if (childPermissions.length === 0) {
    return true;
  }
  
  const result = await Swal.fire({
    title: t('users.permissions.revokeParent'),
    text: t('users.permissions.revokeParentConfirm', { count: childPermissions.length }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('common.actions.confirm'),
    cancelButtonText: t('common.actions.cancel')
  });
  
  if (!result.isConfirmed) {
    permission.isGranted = true;
    return false;
  }
  
  await revokeChildPermissions(childPermissions);
  return true;
};

// Helper: Revoke a permission
const revokePermission = async (permission) => {
  const service = getPermissionService();
  await service.revokePermission(props.userId, permission.name);
  
  Swal.fire({
    title: t('common.actions.success'),
    text: t('users.permissions.revokeSuccess', { permission: permission.displayName || permission.name }),
    icon: 'success',
    timer: 2000,
    showConfirmButton: false
  });
};

// Helper: Handle permission toggle error
const handlePermissionError = (err, permission) => {
  console.error('Error toggling permission:', err);
  permission.isGranted = !permission.isGranted;
  
  if (err?.response?.data?.error?.message) {
    emit('error', err);
  } else {
    Swal.fire({
      title: t('common.actions.error'),
      text: permission.isGranted 
        ? t('users.permissions.revokeFailed') 
        : t('users.permissions.grantFailed'),
      icon: 'error'
    });
  }
};

// Main function: Toggle permission
const togglePermission = async (permission) => {
  if (updating.value) return;
  
  if (!validatePermissionGrant(permission)) {
    return;
  }
  
  updating.value = true;
  
  try {
    if (permission.isGranted) {
      await grantPermission(permission);
    } else {
      const isParent = !permission.parentName;
      const shouldContinue = isParent 
        ? await confirmAndRevokeParent(permission)
        : true;
      
      if (!shouldContinue) {
        updating.value = false;
        return;
      }
      
      await revokePermission(permission);
    }
  } catch (err) {
    handlePermissionError(err, permission);
  } finally {
    updating.value = false;
  }
};

const toggleGroup = (index) => {
  expandedGroups.value[index] = !expandedGroups.value[index];
};

const expandAllGroups = () => {
  // Iterate over all groups in filteredGroupedPermissions to ensure all groups are expanded
  Object.keys(filteredGroupedPermissions.value).forEach((groupName, index) => {
    expandedGroups.value[index] = true;
  });
};

const collapseAllGroups = () => {
  // Iterate over all groups in filteredGroupedPermissions to ensure all groups are collapsed
  Object.keys(filteredGroupedPermissions.value).forEach((groupName, index) => {
    expandedGroups.value[index] = false;
  });
};

// Helper: Toggle a single group permission
const toggleSingleGroupPermission = async (permission, shouldSelect) => {
  if (permission.isGranted === shouldSelect) {
    return;
  }
  
  permission.isGranted = shouldSelect;
  const service = getPermissionService();
  
  if (shouldSelect) {
    await service.grantPermission(props.userId, permission.name);
  } else {
    await service.revokePermission(props.userId, permission.name);
  }
};

// Helper: Toggle all group permissions
const toggleAllGroupPermissions = async (childPermissions, shouldSelect) => {
  for (const permission of childPermissions) {
    await toggleSingleGroupPermission(permission, shouldSelect);
  }
};

// Helper: Revert group permissions on error
const revertGroupPermissions = (childPermissions, shouldSelect) => {
  childPermissions.forEach(permission => {
    permission.isGranted = !shouldSelect;
  });
};

// Helper: Show group toggle success message
const showGroupToggleSuccess = (shouldSelect) => {
  Swal.fire({
    title: t('common.actions.success'),
    text: shouldSelect 
      ? t('users.permissions.groupGrantSuccess') 
      : t('users.permissions.groupRevokeSuccess'),
    icon: 'success',
    timer: 2000,
    showConfirmButton: false
  });
};

// Helper: Handle group toggle error
const handleGroupToggleError = (err, permissions, shouldSelect) => {
  console.error('Error toggling group permissions:', err);
  
  const childPermissions = getChildPermissions(permissions);
  revertGroupPermissions(childPermissions, shouldSelect);
  
  if (err?.response?.data?.error?.message) {
    emit('error', err);
  } else {
    Swal.fire({
      title: t('common.actions.error'),
      text: t('users.permissions.groupToggleFailed'),
      icon: 'error'
    });
  }
};

// Main function: Toggle group selection
const toggleGroupSelection = async (permissions, event) => {
  if (updating.value) return;
  
  const shouldSelect = event.target.checked;
  updating.value = true;
  
  try {
    const childPermissions = getChildPermissions(permissions);
    await toggleAllGroupPermissions(childPermissions, shouldSelect);
    showGroupToggleSuccess(shouldSelect);
  } catch (err) {
    handleGroupToggleError(err, permissions, shouldSelect);
  } finally {
    updating.value = false;
  }
};

const isGroupFullySelected = (permissions) => {
  const childPermissions = getChildPermissions(permissions);
  return childPermissions.length > 0 && childPermissions.every(p => p.isGranted);
};

const isGroupPartiallySelected = (permissions) => {
  const childPermissions = getChildPermissions(permissions);
  const selectedCount = childPermissions.filter(p => p.isGranted).length;
  return selectedCount > 0 && selectedCount < childPermissions.length;
};

// Helper functions for the new UI
const getGroupDisplayName = (groupName, groupPermissions) => {
  // Always search for parent permission in the full permissions list, not just filtered ones
  const parentPermission = permissions.value.find(p => p.name === groupName && !p.parentName);
  if (parentPermission) {
    return parentPermission.displayName || parentPermission.name;
  }
  
  // For other groups, use the group name
  return groupName;
};

const isParentGroup = (groupName) => {
  // Check if this group name corresponds to a parent permission
  return permissions.value.some(p => p.name === groupName && !p.parentName);
};

const getParentPermission = (groupName) => {
  // Get the parent permission for this group
  return permissions.value.find(p => p.name === groupName && !p.parentName);
};

const getChildPermissions = (groupPermissions) => {
  // Filter out the parent permission, only return child permissions
  // Always return child permissions regardless of parent state
  return groupPermissions.filter(p => p.parentName);
};

// Drag and Drop functionality
const draggedGroup = ref(null);
const draggedIndex = ref(null);

const onDragStart = (event, groupName) => {
  draggedGroup.value = groupName;
  draggedIndex.value = Object.keys(filteredGroupedPermissions.value).indexOf(groupName);
  event.dataTransfer.effectAllowed = 'move';
  event.target.style.opacity = '0.5';
};

const onDragEnd = (event) => {
  event.target.style.opacity = '1';
  draggedGroup.value = null;
  draggedIndex.value = null;
  
  // Remove drag-over class from all elements
  document.querySelectorAll('.permission-group').forEach(el => {
    el.classList.remove('drag-over');
  });
};

const onDragOver = (event) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

const onDragEnter = (event, targetGroupName) => {
  event.preventDefault();
  if (draggedGroup.value && draggedGroup.value !== targetGroupName) {
    event.currentTarget.classList.add('drag-over');
  }
};

const onDragLeave = (event) => {
  event.currentTarget.classList.remove('drag-over');
};

const onDrop = (event, targetGroupName) => {
  event.preventDefault();
  event.currentTarget.classList.remove('drag-over');
  
  if (draggedGroup.value && draggedGroup.value !== targetGroupName) {
    const currentGroups = Object.keys(filteredGroupedPermissions.value);
    const targetIndex = currentGroups.indexOf(targetGroupName);
    const draggedGroupName = draggedGroup.value;
    
    // Remove dragged group from current position
    const newOrder = currentGroups.filter(name => name !== draggedGroupName);
    
    // Insert at new position
    newOrder.splice(targetIndex, 0, draggedGroupName);
    
    // Update group order
    groupOrder.value = newOrder;
    saveGroupOrder();
  }
};

// Watch for userId changes to reload permissions
watch(() => props.userId, (newUserId) => {
  if (newUserId && props.show) {
    loadUserPermissions();
  }
});

// Watch for show prop to load permissions when modal opens
watch(() => props.show, (isShown) => {
  if (isShown && props.userId) {
    loadUserPermissions();
  }
});

// Expose loadUserPermissions method for parent component
defineExpose({
  loadUserPermissions
});
</script>

<style scoped>
.permission-card {
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  background-color: #f8f9fa;
  transition: all 0.2s ease;
}

.permission-card:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.permission-tree {
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  background-color: #ffffff;
}

.permission-group {
  border-bottom: 1px solid #f1f3f4;
}

.permission-group:last-child {
  border-bottom: none;
}

.group-header {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.group-toggle {
  cursor: pointer;
  flex-grow: 1;
}

.group-header:hover {
  background-color: #e9ecef;
}

.group-header.expanded {
  background-color: rgba(var(--bs-primary-rgb), 0.1);
  border-bottom-color: var(--bs-primary);
}

.group-content {
  background-color: #ffffff;
}

.permission-items {
  padding: 0.5rem 0;
}

.permission-item {
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  border-bottom: 1px solid #f8f9fa;
  transition: background-color 0.2s ease;
}

.permission-item:hover {
  background-color: #f8f9fa;
}

.permission-item:last-child {
  border-bottom: none;
}

.permission-item.disabled {
  opacity: 0.6;
  background-color: #f8f9fa;
}

.permission-item.disabled .permission-name {
  color: #6c757d;
}

.permission-item.disabled .form-check-input:disabled {
  opacity: 0.5;
}

.permission-item.group-header-item {
  background-color: #f8f9fa;
  border-left: 3px solid #007bff;
  font-style: italic;
}

.permission-item.group-header-item .permission-name {
  color: #007bff;
  font-weight: 600;
}

.permission-name {
  font-weight: 500;
  color: var(--bs-dark);
}

.toggle-icon {
  transition: transform 0.2s ease;
  color: #6c757d;
}

.group-header.expanded .toggle-icon {
  transform: rotate(0deg);
}

.form-check-input:focus {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25);
}

.form-check-input:checked {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.permission-group[draggable="true"] {
  cursor: grab;
  transition: all 0.2s ease;
}

.permission-group[draggable="true"]:active {
  cursor: grabbing;
}

.permission-group[draggable="true"]:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.permission-group.drag-over {
  border: 2px dashed var(--bs-primary);
  background-color: rgba(var(--bs-primary-rgb), 0.05);
  transform: scale(1.02);
}

.permission-group[style*="opacity: 0.5"] {
  transform: rotate(2deg);
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.permission-controls .input-group-text {
  background-color: #f8f9fa;
  border-color: #ced4da;
}
</style> 